<template>
  <div
    class="product-variation-input"
    :class="{
      'product-variation-input--open': isOpen && !isDisabled,
    }"
    :aria-expanded="isOpen && !isDisabled"
    :aria-label="attributeNames.map(attr => $t(`product.variation.attribute.${attr}`)).join(', ')"
    role="combobox"
  >
    <div
      class="product-variation-input__select"
      @click="onToggleSelect"
      @keydown="handleKeyDown"
      role="button"
      tabindex="0"
      aria-controls="product-variation-input-options"
    >
      <div class="product-variation-input__head-row">
        <span
          class="product-variation-input__cell"
          :class="`product-variation-input__cell--${attributeName}`"
          v-for="attributeName in attributeNames"
          :key="attributeName"
        >
          {{ $t(`product.variation.attribute.${attributeName}`) }}
        </span>
      </div>
      <div class="product-variation-input__value-row">
        <span
          class="product-variation-input__cell"
          v-for="attribute in selectedItem.attributes"
          :key="attribute.value"
          v-html="getAttributeValue(attribute)"
        />
      </div>
    </div>

    <div
      class="product-variation-input__options-wrapper"
      v-if="isOpen && !isDisabled"
    >
      <div
        id="product-variation-input-options"
        class="product-variation-input__options"
        aria-live="polite"
        role="listbox"
      >
        <div
          class="product-variation-input__value-row"
          :class="{
            'product-variation-input__value-row--selected': selectedItem.itemId === item.itemId,
            'product-variation-input__value-row--unavailable': !item.availability.available,
            'product-variation-input__value-row--highlighted': highlightedItemIndex === index,
          }"
          v-for="(item, index) in variations.items"
          :key="item.itemId"
          @click="onSelectItem(item)"
          role="listitem"
        >
          <span
            class="product-variation-input__cell"
            v-for="attribute in item.attributes"
            :key="attribute.value"
            v-html="getAttributeValue(attribute)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import {Options, Prop, Vue} from 'vue-property-decorator';
  import type ProductVariation from '../../types/ProductVariation';

  @Options({
    name: 'product-variation-input'
  })
  export default class ProductVariationInput extends Vue {
    @Prop()
    initialSelectedItemId: string;

    @Prop({required: true})
    variations: ProductVariation;

    @Prop({default: false})
    hideSelectedItem: boolean;

    isOpen: boolean = false;
    highlightedItemIndex: number = 0;

    get selectedItem(): { itemId: string, attributes: any[] } | null {
      return this.variations.items.filter((item) => item.itemId === this.initialSelectedItemId)[0];
    }

    get attributeNames(): string[] {
      return this.variations.attributeNames;
    }

    get isDisabled(): boolean {
      return this.variations.items.length <= 1;
    }

    onSelectItem(item: {itemId: string}) {
      if (item.itemId === this.selectedItem.itemId) {
        this.isOpen = false;
        return;
      }
      this.isOpen = false;
      this.$emit('changeVariation', item.itemId);
    }

    getAttributeValue(attribute: {identifier: string, value: string}): string {
      const classes = [
        'product-variation-input__attr',
        `product-variation-input__attr--${attribute.identifier}`
      ];
      return `<span class="${classes.join(' ')}" data-value="${attribute.value}">
        ${attribute.value ? attribute.value : ''}
      </span>`;
    }

    onToggleSelect() {
      if (!this.isOpen) {
        this.openSelect()
      } else {
        this.closeSelect()
      }
    }

    handleKeyDown(event: KeyboardEvent) {
      if (event.code === 'ArrowDown') {
        this.highlightNextItem(event);
      }
      if (event.code === 'ArrowUp') {
        this.highlightPreviousItem(event);
      }
      if (event.code === 'Space') {
        event.preventDefault();
        this.onToggleSelect();
      }
      if (event.code === 'Enter') {
        if (!this.isOpen) {
          this.openSelect();
        } else {
          event.preventDefault();
          this.onSelectItem(this.variations.items[this.highlightedItemIndex]);
        }
      }
      if (event.code === 'Tab') {
        this.closeSelect();
      }
    }

    highlightNextItem(event: KeyboardEvent) {
      event.preventDefault();
      if (!this.isOpen) {
        this.openSelect();
      }
      const selectedItemIndex = this.variations.items.findIndex(
        (item) => item.itemId === this.selectedItem.itemId
      );

      this.highlightedItemIndex = (this.highlightedItemIndex + 1) % this.variations.items.length;
      // skip selected item
      if (this.hideSelectedItem && this.highlightedItemIndex === selectedItemIndex) {
        this.highlightedItemIndex = (this.highlightedItemIndex + 1) % this.variations.items.length;
      }
    }

    highlightPreviousItem(event: KeyboardEvent) {
      event.preventDefault();
      if (!this.isOpen) {
        this.openSelect();
      }
      const selectedItemIndex = this.variations.items.findIndex(
        (item) => item.itemId === this.selectedItem.itemId
      );

      this.highlightedItemIndex = (this.highlightedItemIndex - 1 + this.variations.items.length) % this.variations.items.length;
      // skip selected item
      if (this.hideSelectedItem && this.highlightedItemIndex === selectedItemIndex) {
        this.highlightedItemIndex = (this.highlightedItemIndex - 1 + this.variations.items.length) % this.variations.items.length;
      }
    }

    openSelect() {
      this.isOpen = true;
      setTimeout(() => {
        document.body.addEventListener('click', this.closeSelect);
      })
    }

    closeSelect() {
      this.isOpen = false;
      document.body.removeEventListener('click', this.closeSelect)
    }
  }
</script>
